// src/App.js

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { TasksProvider } from "./context/TasksContext";

import {
  ScrollToTopOnRouteChange,
  ScrollToTopButton,
} from "./components/ScrollHelpers";

import ProtectedRoute from "./components/ProtectedRoute";
import {
  UserDashboardLayout,
  AdminDashboardLayout,
  RoleDashboardLayout,
} from "./components/DashboardLayout";

/* -------- PAGES -------- */
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

import UserDashboardPage from "./pages/UserDashboardPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";

import ProfilePage from "./pages/ProfilePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import HelpPage from "./pages/HelpPage";
import PharmacyReportPage from "./pages/PharmacyReportPage";
import PharmacyInventoryPage from "./pages/PharmacyInventoryPage";

import UploadPage from "./pages/UpLoadPage";
import OrdersPage from "./pages/OrdersPage";
import OrdersLeafletPage from "./pages/OrdersLeafletPage";

import OhrozenciPage from "./pages/OhrozenciPage";
import LezakyPage from "./pages/LezakyPage";
import OverstockPage from "./pages/OverstockPage";
import RemoteStocksPage from "./pages/RemoteStocksPage";
import NormMismatchPage from "./pages/NormMismatchPage";
import DiscountedPage from "./pages/DiscountedPage";

import WatchlistsPage from "./pages/WatchlistsPage";
import WatchlistDetailPage from "./pages/WatchlistDetailPage";

import UserTasksPage from "./pages/UserTasksPage";
import AdminTasksPage from "./pages/AdminTasksPage";

import PoolPage from "./pages/PoolPage";
import PoolStatsPage from "./pages/PoolStatsPage";
import AdminPoolItemsPage from "./pages/AdminPoolItemsPage";

import AkcniCenyPage from "./pages/AkcniCenyPage";
import AdminLeafletUploadPage from "./pages/AdminLeafletUpload";
import AdminUhradyUploadPage from "./pages/AdminUhradyUpload";

import PoukazkyUploadPage from "./pages/PoukazkyUploadPage";
import PoukazkyReportPage from "./pages/PoukazkyReportPage";

import IncomingTransfersPage from "./pages/IncomingTransfersPage";
import OutgoingTransfersPage from "./pages/OutgoingTransfersPage";
import ConfirmedTransfersPage from "./pages/ConfirmedTransfersPage";
import PreparedTransfersPage from "./pages/PreparedTransfersPage";
import ReceivedTransfersPage from "./pages/ReceivedTransfersPage";
import ShippedTransfersPage from "./pages/ShippedTransfersPage";
import ArchiveTransfersPage from "./pages/ArchiveTransfersPage";
import RefusedPage from "./pages/RefusedPage";

import LedgerUserPage from "./pages/LedgerUserPage";
import LedgerAdminPage from "./pages/LedgerAdminPage";

import AdminUsersPage from "./pages/AdminUsersPage";
import AdminDataExplorer from "./pages/AdminDataExplorer";

/** HR moduly */
import EmployeesPage from "./pages/EmployeesPage";
import LeaveRequestPage from "./pages/LeaveRequestPage";
import LeavesOverviewPage from "./pages/LeavesOverviewPage";
import ApproveLeavesPage from "./pages/ApproveLeavesPage";
import TestCalendar from "./pages/TestCalendar";
import ShiftsPage from "./pages/ShiftsPage";
import BranchCalendarPage from "./pages/BranchCalendarPage";
import ApproveShiftsPage from "./pages/ApproveShiftsPage";
import ShiftPlansHistoryPage from "./pages/ShiftPlansHistoryPage";
import PersonalShiftsHistoryPage from "./pages/PersonalShiftsHistoryPage";
import ApprovedShiftsPrintPage from "./pages/ApprovedShiftsPrintPage";
import ShiftOverviewPage from "./pages/ShiftOverviewPage";
import ShiftRisksPage from "./pages/ShiftRisksPage";
import TrainingRecordsPage from "./pages/TrainingRecordsPage";
import TrainingDefinitionsPage from "./pages/TrainingDefinitionsPage";

/* ------------------------- */

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <TasksProvider>
          <ScrollToTopOnRouteChange />

          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

            {/* ALIASY DASHBOARDŮ */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <DashboardRoleRedirect />
                </ProtectedRoute>
              }
            />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/user" element={<Navigate to="/user/dashboard" replace />} />
            <Route path="/rm" element={<Navigate to="/rm/dashboard" replace />} />

            {/* USER DASHBOARD */}
            <Route
              path="/user/dashboard"
              element={
                <ProtectedRoute roles={["user"]}>
                  <UserDashboardLayout>
                    <UserDashboardPage />
                  </UserDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* RM DASHBOARD */}
            <Route
              path="/rm/dashboard"
              element={
                <ProtectedRoute roles={["rm"]}>
                  <AdminDashboardLayout>
                    <UserDashboardPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN DASHBOARD */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <AdminDashboardPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* -------------------------------------------------- */}
            {/*                 SHARED ROUTES                     */}
            {/* -------------------------------------------------- */}
            {/* SPRÁVA NÁSTĚNKY (admin + rm) */}
            <Route
              path="/announcements"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <AdminDashboardPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />
            {/* USER/RM profil */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <ProfilePage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN profil */}
            <Route
              path="/admin/profile"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <ProfilePage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <TrainingRecordsPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <ChangePasswordPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <HelpPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/calendar"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <BranchCalendarPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* WATCHLISTS */}
            <Route
              path="/watchlists"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <WatchlistsPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/watchlists/:firma/:pharmacyCode/:id"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <WatchlistDetailPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* PHARMACY PAGES */}
            {/* USER/RM report */}
            <Route
              path="/pharmacy/report"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <PharmacyReportPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN report */}
            <Route
              path="/admin/pharmacy/report"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <PharmacyReportPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/pharmacy/inventory"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <PharmacyInventoryPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pharmacy/ohrozenci"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <OhrozenciPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pharmacy/lezaky"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <LezakyPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ORDERS */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <OrdersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/leaflet"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <OrdersLeafletPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* MISC */}
            <Route
              path="/overstock"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <OverstockPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounted"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <DiscountedPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/norm-mismatch"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <NormMismatchPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/test-calendar" element={<TestCalendar />} />

            {/* USER/RM plánovač */}
            <Route
              path="/shifts"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <ShiftsPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN plánovač */}
            <Route
              path="/admin/shifts"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <ShiftsPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks"
              element={
                <ProtectedRoute roles={["user"]}>
                  <UserDashboardLayout>
                    <UserTasksPage />
                  </UserDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/remote-stocks"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <RemoteStocksPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* EXPORT UPLOAD */}
            <Route
              path="/upload"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <UploadPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* -------------- POOL (USER + ADMIN) ---------------- */}
            <Route
              path="/pool"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <PoolPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* POOL STATS (ADMIN ONLY) */}
            <Route
              path="/pool-stats"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <PoolStatsPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN – Správa POOL položek */}
            <Route
              path="/admin/pool-items"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <AdminPoolItemsPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* -------------- LEDGER ---------------- */}
            <Route
              path="/ledger"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <LedgerUserPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ledger-admin"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <LedgerAdminPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* -------------- TRANSFERS ---------------- */}
            <Route
              path="/transfers/incoming"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <IncomingTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfers/outgoing"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <OutgoingTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfers/confirmed"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <ConfirmedTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfers/prepared"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <PreparedTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfers/received"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <ReceivedTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfers/shipped"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <ShippedTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfers/archive"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <ArchiveTransfersPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* REFUSED */}
            <Route
              path="/refused"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <RefusedPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ---------------- POUKÁZKY ---------------- */}
            <Route
              path="/poukazky-upload"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <PoukazkyUploadPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/poukazky-report"
              element={
                <ProtectedRoute roles={["user", "rm"]}>
                  <RoleDashboardLayout>
                    <PoukazkyReportPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* -------------- ADMIN ONLY ---------------- */}
            <Route
              path="/admin/leaflet-upload"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <AdminLeafletUploadPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/uhrady-upload"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <AdminUhradyUploadPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/tasks"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <AdminTasksPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/data-explorer"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <AdminDataExplorer />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AdminDashboardLayout>
                    <AdminUsersPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN AKČNÍ CENY */}
            <Route
              path="/akcni-ceny"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <AkcniCenyPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ---------------- HR / PERSONAL ---------------- */}

            {/* USER – zaměstnanci pobočky + viditelní létači */}
            <Route
              path="/employees"
              element={
                <ProtectedRoute roles={["user"]}>
                  <UserDashboardLayout>
                    <EmployeesPage />
                  </UserDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN/RM – zaměstnanci */}
            <Route
              path="/admin/personal/employees"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <EmployeesPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/personal/training-definitions"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <TrainingDefinitionsPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />
            {/* USER / RM – žádost o volno */}
            <Route
              path="/personal/leaves"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <LeaveRequestPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* USER / RM – přehled vlastních žádostí */}
            <Route
              path="/personal/leaves-overview"
              element={
                <ProtectedRoute roles={["user", "rm", "admin"]}>
                  <RoleDashboardLayout>
                    <LeavesOverviewPage />
                  </RoleDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/personal/shifts-overview"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <ShiftOverviewPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/personal/shifts-risks"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <ShiftRisksPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/personal/approve"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <ApproveLeavesPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* USER – historie plánů směn (pobočková, read-only) */}
            <Route
              path="/personal/shifts-history"
              element={
                <ProtectedRoute roles={["user"]}>
                  <UserDashboardLayout>
                    <PersonalShiftsHistoryPage />
                  </UserDashboardLayout>
                </ProtectedRoute>
              }
            />
            {/* USER – tisk schváleného plánu (A4 landscape, read-only) */}
            <Route
              path="/personal/shifts-print"
              element={
                <ProtectedRoute roles={["user"]}>
                  <UserDashboardLayout>
                    <ApprovedShiftsPrintPage />
                  </UserDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/personal/approve/shifts"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <ApproveShiftsPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/personal/shifts-history"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <ShiftPlansHistoryPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/personal/leaves-overview"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <LeavesOverviewPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN – vytvoření žádosti za zaměstnance */}
            <Route
              path="/admin/personal/leaves"
              element={
                <ProtectedRoute roles={["admin", "rm"]}>
                  <AdminDashboardLayout>
                    <LeaveRequestPage />
                  </AdminDashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route
              path="*"
              element={<div className="p-6">404 – stránka nenalezena</div>}
            />
          </Routes>

          <ScrollToTopButton />
        </TasksProvider>
      </Router>
    </AuthProvider>
  );
}

/**
 * Přesměrování /dashboard podle role:
 * - admin -> /admin/dashboard
 * - rm -> /rm/dashboard
 * - user -> /user/dashboard
 */
function DashboardRoleRedirect() {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === "admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (user.role === "rm") {
    return <Navigate to="/rm/dashboard" replace />;
  }

  return <Navigate to="/user/dashboard" replace />;
}